import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Settings, ArrowLeft, Save, Upload, Image as ImageIcon, AlertTriangle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GeneralSettings = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  const [settings, setSettings] = useState({
    app_name: 'OddsChamp',
    company_name: 'OddsChamp',
    contact_email: 'support@oddschamp.com',
    support_email: 'support@oddschamp.com',
    app_logo_url: '',
    app_icon_url: '',
    facebook_url: '',
    twitter_url: '',
    instagram_url: '',
    linkedin_url: '',
    privacy_policy: '',
    terms_of_use: '',
    maintenance_mode: false,
    maintenance_message: 'We are currently performing scheduled maintenance. Please check back soon.'
  });

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/admin/general-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        // Construct full URLs for logo and icon
        const settingsWithFullUrls = {
          ...response.data,
          app_logo_url: response.data.app_logo_url ? 
            (response.data.app_logo_url.startsWith('http') ? response.data.app_logo_url : `${BACKEND_URL}${response.data.app_logo_url}`) : '',
          app_icon_url: response.data.app_icon_url ? 
            (response.data.app_icon_url.startsWith('http') ? response.data.app_icon_url : `${BACKEND_URL}${response.data.app_icon_url}`) : ''
        };
        setSettings(prev => ({ ...prev, ...settingsWithFullUrls }));
      }
    } catch (error) {
      console.error('Error fetching general settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.post(`${API}/admin/general-settings`, {
        app_name: settings.app_name,
        company_name: settings.company_name,
        contact_email: settings.contact_email,
        support_email: settings.support_email,
        facebook_url: settings.facebook_url,
        twitter_url: settings.twitter_url,
        instagram_url: settings.instagram_url,
        linkedin_url: settings.linkedin_url,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('General settings saved successfully!');
    } catch (error) {
      console.error('Error saving general settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomPages = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.post(`${API}/admin/general-settings`, {
        privacy_policy: settings.privacy_policy,
        terms_of_use: settings.terms_of_use,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Custom pages saved successfully!');
    } catch (error) {
      console.error('Error saving custom pages:', error);
      toast.error(error.response?.data?.detail || 'Failed to save custom pages');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMaintenance = async () => {
    const token = localStorage.getItem('session_token');
    const newValue = !settings.maintenance_mode;
    
    try {
      await axios.post(`${API}/admin/general-settings`, {
        maintenance_mode: newValue,
        maintenance_message: settings.maintenance_message,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings({ ...settings, maintenance_mode: newValue });
      toast.success(newValue ? 'Maintenance mode ENABLED - Regular users will see the maintenance page' : 'Maintenance mode DISABLED - Site is now live');
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      toast.error(error.response?.data?.detail || 'Failed to update maintenance mode');
    }
  };

  const handleSaveMaintenanceMessage = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.post(`${API}/admin/general-settings`, {
        maintenance_message: settings.maintenance_message,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Maintenance message saved!');
    } catch (error) {
      console.error('Error saving maintenance message:', error);
      toast.error(error.response?.data?.detail || 'Failed to save maintenance message');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBranding = async (type) => {
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.delete(`${API}/admin/general-settings/branding/${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (type === 'logo') {
        setSettings({ ...settings, app_logo_url: '' });
      } else {
        setSettings({ ...settings, app_icon_url: '' });
      }
      
      toast.success(`${type === 'logo' ? 'Logo' : 'Icon'} deleted successfully!`);
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type); // 'logo' or 'icon'

    const token = localStorage.getItem('session_token');
    
    try {
      const response = await axios.post(`${API}/admin/general-settings/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Construct full URL with backend URL
      const fullUrl = `${BACKEND_URL}${response.data.url}`;
      
      if (type === 'logo') {
        setSettings({ ...settings, app_logo_url: fullUrl });
      } else {
        setSettings({ ...settings, app_icon_url: fullUrl });
      }
      
      toast.success(`${type === 'logo' ? 'Logo' : 'Icon'} uploaded successfully!`);
      
      // Refresh settings to get updated URLs
      await fetchSettings();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 flex items-center justify-center">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center mb-8">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl sm:text-4xl font-bold text-white">General Settings</h1>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 flex-wrap">
            <Button
              onClick={() => setActiveTab('general')}
              variant={activeTab === 'general' ? 'default' : 'outline'}
              className={activeTab === 'general' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              General Info
            </Button>
            <Button
              onClick={() => setActiveTab('branding')}
              variant={activeTab === 'branding' ? 'default' : 'outline'}
              className={activeTab === 'branding' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              Branding
            </Button>
            <Button
              onClick={() => setActiveTab('custom-pages')}
              variant={activeTab === 'custom-pages' ? 'default' : 'outline'}
              className={activeTab === 'custom-pages' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              Custom Pages
            </Button>
            <Button
              onClick={() => setActiveTab('maintenance')}
              variant={activeTab === 'maintenance' ? 'default' : 'outline'}
              className={activeTab === 'maintenance' ? 'bg-orange-600 hover:bg-orange-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              🔧 Maintenance
            </Button>
          </div>

          {/* General Info Tab */}
          {activeTab === 'general' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Settings className="w-6 h-6 mr-2 text-blue-400" />
                  General Information
                </CardTitle>
                <p className="text-gray-400 text-sm">Configure basic application settings</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveGeneral} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* App Name */}
                    <div>
                      <Label className="text-gray-300">App Name</Label>
                      <Input
                        type="text"
                        value={settings.app_name}
                        onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="OddsChamp"
                      />
                      <p className="text-xs text-gray-500 mt-1">Displayed in page titles, navigation, login/signup pages</p>
                    </div>

                    {/* Company Name */}
                    <div>
                      <Label className="text-gray-300">Company Name</Label>
                      <Input
                        type="text"
                        value={settings.company_name}
                        onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="OddsChamp"
                      />
                      <p className="text-xs text-gray-500 mt-1">Used in footer copyright</p>
                    </div>

                    {/* Contact Email */}
                    <div>
                      <Label className="text-gray-300">Contact Email</Label>
                      <Input
                        type="email"
                        value={settings.contact_email}
                        onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="support@oddschamp.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">Displayed in footer with email icon</p>
                    </div>

                    {/* Support Email */}
                    <div>
                      <Label className="text-gray-300">Support Email</Label>
                      <Input
                        type="email"
                        value={settings.support_email}
                        onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="support@oddschamp.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">Support ticket email</p>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-6">
                    <h3 className="text-white font-semibold mb-2">Social Media Links</h3>
                    <p className="text-xs text-gray-500 mb-4">Social icons will appear in the footer when URLs are added</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Facebook */}
                      <div>
                        <Label className="text-gray-300">Facebook URL</Label>
                        <Input
                          type="url"
                          value={settings.facebook_url}
                          onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="https://facebook.com/yourpage"
                        />
                      </div>

                      {/* Twitter */}
                      <div>
                        <Label className="text-gray-300">Twitter/X URL</Label>
                        <Input
                          type="url"
                          value={settings.twitter_url}
                          onChange={(e) => setSettings({ ...settings, twitter_url: e.target.value })}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="https://twitter.com/yourhandle"
                        />
                      </div>

                      {/* Instagram */}
                      <div>
                        <Label className="text-gray-300">Instagram URL</Label>
                        <Input
                          type="url"
                          value={settings.instagram_url}
                          onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="https://instagram.com/yourprofile"
                        />
                      </div>

                      {/* LinkedIn */}
                      <div>
                        <Label className="text-gray-300">LinkedIn URL</Label>
                        <Input
                          type="url"
                          value={settings.linkedin_url}
                          onChange={(e) => setSettings({ ...settings, linkedin_url: e.target.value })}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="https://linkedin.com/company/yourcompany"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save General Settings'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <ImageIcon className="w-6 h-6 mr-2 text-blue-400" />
                  Branding & Appearance
                </CardTitle>
                <p className="text-gray-400 text-sm">Upload your logo and icon</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  {/* Logo Upload */}
                  <div>
                    <Label className="text-gray-300 text-lg mb-3 block">App Logo</Label>
                    <p className="text-sm text-gray-500 mb-4">
                      Recommended size: 200x50px. Displayed in navigation header, landing page, login/signup pages. If no logo is uploaded, app name with icon will be shown.
                    </p>
                    
                    {settings.app_logo_url && (
                      <div className="mb-4 p-4 bg-white/5 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-xs text-gray-400">Current Logo:</p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBranding('logo')}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-6 px-2"
                          >
                            Delete
                          </Button>
                        </div>
                        <img 
                          src={settings.app_logo_url} 
                          alt="App Logo" 
                          className="max-h-16 object-contain bg-white/10 p-2 rounded"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'logo')}
                        className="bg-white/5 border-white/10 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                      />
                      <Upload className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Icon Upload */}
                  <div className="border-t border-white/10 pt-8">
                    <Label className="text-gray-300 text-lg mb-3 block">App Icon / Favicon</Label>
                    <p className="text-sm text-gray-500 mb-4">
                      Recommended size: 512x512px. Displayed as browser favicon (tab icon). Automatically updated when uploaded.
                    </p>
                    
                    {settings.app_icon_url && (
                      <div className="mb-4 p-4 bg-white/5 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-xs text-gray-400">Current Icon:</p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBranding('icon')}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-6 px-2"
                          >
                            Delete
                          </Button>
                        </div>
                        <img 
                          src={settings.app_icon_url} 
                          alt="App Icon" 
                          className="w-16 h-16 object-contain bg-white/10 p-2 rounded"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'icon')}
                        className="bg-white/5 border-white/10 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                      />
                      <Upload className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>

                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm text-blue-200">
                      <strong>Note:</strong> Logo and icon files are automatically optimized and stored. 
                      Maximum file size: 2MB. Supported formats: JPG, PNG, SVG, WebP.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Custom Pages Tab */}
          {activeTab === 'custom-pages' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Custom Pages Content</CardTitle>
                <p className="text-gray-400 text-sm">Edit Privacy Policy and Terms of Use content</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveCustomPages} className="space-y-6">
                  {/* Privacy Policy */}
                  <div>
                    <Label className="text-gray-300">Privacy Policy</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      HTML content for your privacy policy page. Leave empty to use default content.
                    </p>
                    <Textarea
                      value={settings.privacy_policy}
                      onChange={(e) => setSettings({ ...settings, privacy_policy: e.target.value })}
                      className="bg-white/5 border-white/10 text-white font-mono text-sm min-h-[300px]"
                      placeholder="<h2>Privacy Policy</h2><p>Your privacy policy content here...</p>"
                    />
                  </div>

                  {/* Terms of Use */}
                  <div>
                    <Label className="text-gray-300">Terms of Use</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      HTML content for your terms of use page. Leave empty to use default content.
                    </p>
                    <Textarea
                      value={settings.terms_of_use}
                      onChange={(e) => setSettings({ ...settings, terms_of_use: e.target.value })}
                      className="bg-white/5 border-white/10 text-white font-mono text-sm min-h-[300px]"
                      placeholder="<h2>Terms of Use</h2><p>Your terms of use content here...</p>"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Custom Pages'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Maintenance Mode Tab */}
          {activeTab === 'maintenance' && (
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <AlertTriangle className="w-6 h-6 mr-2 text-orange-400" />
                  Maintenance Mode
                </CardTitle>
                <p className="text-gray-400 text-sm">Enable maintenance mode to restrict access while you make changes</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status Banner */}
                <div className={`p-4 rounded-lg border ${settings.maintenance_mode ? 'bg-orange-500/20 border-orange-500/50' : 'bg-green-500/20 border-green-500/50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${settings.maintenance_mode ? 'bg-orange-500 animate-pulse' : 'bg-green-500'}`}></div>
                      <div>
                        <p className={`font-semibold ${settings.maintenance_mode ? 'text-orange-300' : 'text-green-300'}`}>
                          {settings.maintenance_mode ? 'MAINTENANCE MODE ACTIVE' : 'SITE IS LIVE'}
                        </p>
                        <p className="text-sm text-gray-400">
                          {settings.maintenance_mode 
                            ? 'Regular users see the maintenance page. Admins have full access.' 
                            : 'All users can access the site normally.'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.maintenance_mode}
                      onCheckedChange={handleToggleMaintenance}
                      className="data-[state=checked]:bg-orange-500"
                    />
                  </div>
                </div>

                {/* Admin Access Info */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-blue-300 font-medium">Admin Access</p>
                      <p className="text-sm text-gray-400 mt-1">
                        When maintenance mode is enabled, <strong>admin users can still login and access all features</strong>. 
                        This allows you to test payments, make changes, and verify everything works before going live.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Maintenance Message */}
                <form onSubmit={handleSaveMaintenanceMessage} className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Maintenance Message</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      This message will be displayed to users during maintenance.
                    </p>
                    <Textarea
                      value={settings.maintenance_message}
                      onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                      className="bg-white/5 border-white/10 text-white min-h-[120px]"
                      placeholder="We are currently performing scheduled maintenance. Please check back soon."
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Message'}
                  </Button>
                </form>

                {/* Preview */}
                {settings.maintenance_mode && (
                  <div className="mt-6">
                    <Label className="text-gray-300 mb-2 block">Maintenance Page Preview</Label>
                    <div className="bg-slate-800 rounded-lg p-8 text-center border border-white/10">
                      <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertTriangle className="w-8 h-8 text-orange-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-2">Under Maintenance</h2>
                      <p className="text-gray-400 max-w-md mx-auto">{settings.maintenance_message}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GeneralSettings;
